import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Calendar,
  Tag,
  Mail,
  MessageSquare,
  Phone,
  Globe,
  Clock,
  Save,
  RotateCcw,
  ChevronDown,
  Check,
} from 'lucide-react';
import { Button, Badge } from '@centraoneplus/ui';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { useSavedFiltersStore } from '@stores/savedFiltersStore';
import {
  SavedFiltersSection,
  SaveFilterButton,
  type SavedFilter as UISavedFilter,
} from '@centraoneplus/ui';
import type { SavedFilter } from '@services/saved-filters.service';
import { ticketTypesService } from '@services/ticket-types.service';
import { ticketService } from '@services/ticket.service';
import type { TicketType, TicketOriginOption } from '@centraoneplus/shared-types';
import { getTicketSourceDisplay, getTicketSourceIcon } from './TicketSourceBadge';
import { useAuthStore } from '@stores/authStore';
import { teamMembersService, type TeamMember } from '@services/team-members.service';
import { teamsService } from '@services/teams.service';
import { tagService } from '@services/tag.service';
import { ThemedSelect } from '@components/common/ThemedSelect';
import {
  isParentTeam,
  orderTeamsForPicker,
  teamOptionLabel,
  teamsInViewerScope,
} from '@components/settings/TeamsSettings/teamHierarchy';
import type { TeamWithMembers } from '../../types/team';

export interface TicketFilters {
  status?: string[];
  priority?: string[];
  channel?: string[];
  /**
   * Multi-select assignee filter. Holds agent UUIDs plus the literal
   * 'unassigned' sentinel for the "no assignee" option — mirroring how
   * status/priority/tags are modelled. Empty/undefined means "Anyone".
   */
  assigneeIds?: string[];
  /**
   * "Assigned to me" quick filter — show ONLY tickets assigned to the current
   * user, for every role (agents included). Resolved to the caller's own id in
   * `buildApiFilters`, which emits it as the *plural* `assigneeIds` param so the
   * BFF's agent auto-scope does not widen the result with the unassigned queue
   * (it only adds `includeUnassigned` when no `assigneeIds` is present). When on,
   * it overrides the `assigneeIds` multi-select above.
   */
  assignedToMe?: boolean;
  /**
   * Which role the assignee filter (or "Assigned to me") matches. Unset means
   * either: the assignee or a secondary agent working the ticket.
   */
  assigneeRole?: 'primary' | 'secondary';
  /**
   * Multi-select ticket-type filter, holding ticket type UUIDs — modelled the
   * same way as status/priority/tags. Empty/undefined means "Any type".
   */
  ticketTypeIds?: string[];
  /**
   * Team filter. A ticket belongs to a team when it carries that team, or
   * carries none and is held by one of its agents. Non-admins can only pick
   * their own teams, and it narrows what they already see.
   */
  teamId?: string;
  /**
   * With a parent team picked: include its sub-teams (on by default). Off
   * shows only the parent's own queue.
   */
  includeSubTeams?: boolean;
  /**
   * Multi-select ticket-source filter: origin slugs from GET /tickets/origins
   * (a product like 'control-tower', or 'email', 'portal', 'agent', ...).
   */
  origins?: string[];
  tags?: string[];
  dateRange?: {
    from: Date | string;
    to: Date | string;
  };
  slaStatus?: 'breached' | 'at_risk' | 'on_track';
  search?: string;
}

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
  { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300' },
  { value: 'resolved', label: 'Resolved', color: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  { value: 'closed', label: 'Closed', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/40 dark:text-orange-300' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300' },
];

const CHANNEL_OPTIONS = [
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'chat', label: 'Chat', icon: MessageSquare },
  { value: 'phone', label: 'Phone', icon: Phone },
  { value: 'web', label: 'Web Portal', icon: Globe },
];

const SLA_OPTIONS = [
  { value: 'breached', label: 'SLA Breached', color: 'text-red-600' },
  { value: 'at_risk', label: 'At Risk', color: 'text-amber-600' },
  { value: 'on_track', label: 'On Track', color: 'text-green-600' },
];

const DATE_PRESETS = [
  { label: 'Today', getValue: () => ({ from: startOfDay(new Date()), to: endOfDay(new Date()) }) },
  { label: 'Yesterday', getValue: () => ({ from: startOfDay(subDays(new Date(), 1)), to: endOfDay(subDays(new Date(), 1)) }) },
  { label: 'Last 7 days', getValue: () => ({ from: startOfDay(subDays(new Date(), 7)), to: endOfDay(new Date()) }) },
  { label: 'Last 30 days', getValue: () => ({ from: startOfDay(subDays(new Date(), 30)), to: endOfDay(new Date()) }) },
  { label: 'Last 90 days', getValue: () => ({ from: startOfDay(subDays(new Date(), 90)), to: endOfDay(new Date()) }) },
];

/** Deserialize date strings back to Date objects when loading a saved filter */
function deserializeFilters(filters: Record<string, any>): TicketFilters {
  const result = { ...filters } as TicketFilters;
  if (result.dateRange) {
    result.dateRange = {
      from: new Date(result.dateRange.from),
      to: new Date(result.dateRange.to),
    };
  }
  // Back-compat: older saved filters stored a single `assigneeId`; the panel
  // now uses a multi-select `assigneeIds` array. Migrate on load.
  const legacyAssignee = (result as Record<string, any>).assigneeId;
  if (legacyAssignee && !result.assigneeIds) {
    result.assigneeIds = [legacyAssignee];
  }
  delete (result as Record<string, any>).assigneeId;
  // Back-compat: older saved filters stored a single `ticketTypeId`; the panel
  // now uses a multi-select `ticketTypeIds` array.
  const legacyTicketType = (result as Record<string, any>).ticketTypeId;
  if (legacyTicketType && !result.ticketTypeIds) {
    result.ticketTypeIds = [legacyTicketType];
  }
  delete (result as Record<string, any>).ticketTypeId;
  return result;
}

/**
 * Translate the panel's multi-select `assigneeIds` into the query params the
 * API understands. Shared by the board and list views so both filter
 * identically:
 *   - specific agents        → assigneeId (one) / assigneeIds (many, csv)
 *   - "Unassigned" only      → unassigned=true
 *   - agents + "Unassigned"  → assignee filter + includeUnassigned (OR semantics)
 */
export function buildAssigneeParams(assigneeIds?: string[]): {
  assigneeId?: string;
  assigneeIds?: string;
  unassigned?: boolean;
  includeUnassigned?: boolean;
} {
  const ids = assigneeIds ?? [];
  const hasUnassigned = ids.includes('unassigned');
  const realIds = ids.filter((id) => id !== 'unassigned');

  const assigneeScope =
    realIds.length === 1
      ? { assigneeId: realIds[0] }
      : realIds.length > 1
        ? { assigneeIds: realIds.join(',') }
        : {};

  if (realIds.length > 0) {
    return hasUnassigned ? { ...assigneeScope, includeUnassigned: true } : assigneeScope;
  }
  if (hasUnassigned) return { unassigned: true };
  return {};
}

/**
 * Translate the panel's `TicketFilters` (+ the free-text search box) into the
 * query params the ticket API understands. Shared by the board and the list so
 * both views filter identically — the backend's `buildWhereFromQuery` is the
 * same for `/tickets` and `/tickets/board`.
 *
 * Note: only truthy keys are emitted. Callers that MERGE this into existing
 * state (the list store) must reset the filter-owned keys first so clearing a
 * filter actually removes it — see FILTER_RESET_KEYS.
 */
export function buildApiFilters(
  filters: TicketFilters,
  search: string,
  currentUserId?: string,
): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  if (search) params.search = search;
  if (filters.status?.length) params.status = filters.status;
  if (filters.priority?.length) params.priority = filters.priority;
  if (filters.channel?.length) params.channel = filters.channel;
  if (filters.tags?.length) params.tags = filters.tags;
  // "Assigned to me" is a strict override: emit the current user's id as the
  // PLURAL assigneeIds param (not the singular assigneeId that a single-select
  // produces) so agents get ONLY their own tickets — the BFF skips the
  // unassigned-queue widening whenever assigneeIds is present. Falls back to the
  // regular assignee multi-select when the flag is off or the id is unknown.
  if (filters.assignedToMe && currentUserId) {
    params.assigneeIds = currentUserId;
  } else {
    Object.assign(params, buildAssigneeParams(filters.assigneeIds));
  }
  // Secondary agents: an assignee filter matches the assignee or a secondary
  // agent unless narrowed. Agents send no assignee param (the BFF scopes them
  // to themselves, as either), so only a narrowing is sent for them.
  if (filters.assigneeRole) params.assigneeMatch = filters.assigneeRole;
  else if (params.assigneeIds || params.assigneeId) params.assigneeMatch = 'any';
  // Ticket type: one type → ticketTypeId, many → comma-separated ticketTypeIds.
  const typeIds = filters.ticketTypeIds ?? [];
  if (typeIds.length === 1) params.ticketTypeId = typeIds[0];
  else if (typeIds.length > 1) params.ticketTypeIds = typeIds.join(',');
  // Source: the API takes one comma-separated `origin` list.
  if (filters.origins?.length) params.origin = filters.origins.join(',');
  if (filters.teamId) {
    params.teamId = filters.teamId;
    if (filters.includeSubTeams) params.includeSubTeams = true;
  }
  if (filters.dateRange?.from) {
    params.createdAfter =
      filters.dateRange.from instanceof Date
        ? filters.dateRange.from.toISOString()
        : filters.dateRange.from;
  }
  if (filters.dateRange?.to) {
    params.createdBefore =
      filters.dateRange.to instanceof Date
        ? filters.dateRange.to.toISOString()
        : filters.dateRange.to;
  }
  // Backend only exposes a boolean SLA-breach flag; 'at_risk'/'on_track' aren't
  // filterable server-side today.
  if (filters.slaStatus === 'breached') params.slaBreached = true;
  return params;
}

/**
 * Every filter-owned key buildApiFilters can produce. Callers that merge into a
 * persistent filter store spread this (all undefined) BEFORE the built params so
 * a cleared filter is removed rather than lingering from the previous apply.
 */
export const FILTER_RESET_KEYS = {
  search: undefined,
  status: undefined,
  priority: undefined,
  channel: undefined,
  tags: undefined,
  ticketTypeId: undefined,
  ticketTypeIds: undefined,
  origin: undefined,
  teamId: undefined,
  includeSubTeams: undefined,
  assigneeId: undefined,
  assigneeIds: undefined,
  unassigned: undefined,
  includeUnassigned: undefined,
  assigneeMatch: undefined,
  createdAfter: undefined,
  createdBefore: undefined,
  slaBreached: undefined,
} as const;

/** Convert Date objects to ISO strings for storage */
function serializeFilters(filters: TicketFilters): Record<string, any> {
  const result = { ...filters } as Record<string, any>;
  if (result.dateRange) {
    result.dateRange = {
      from: result.dateRange.from instanceof Date ? result.dateRange.from.toISOString() : result.dateRange.from,
      to: result.dateRange.to instanceof Date ? result.dateRange.to.toISOString() : result.dateRange.to,
    };
  }
  return result;
}

interface MultiSelectProps {
  label: string;
  options: { value: string; label: string; color?: string; icon?: React.ElementType }[];
  selected: string[];
  onChange: (selected: string[]) => void;
  /** Render a search box to filter options (used for the free-form Tags list). */
  searchable?: boolean;
}

function MultiSelect({ label, options, selected, onChange, searchable = false }: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Menu is portalled to <body> with fixed positioning so it floats above the
  // filter panel instead of being clipped by its scroll container. Positioned
  // from the trigger's rect, flipping upward when there's no room below.
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  const MENU_MAX_H = 288; // 18rem

  const openMenu = () => {
    const el = buttonRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUp = spaceBelow < MENU_MAX_H && spaceAbove > spaceBelow;
      setMenuStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        maxHeight: Math.min(MENU_MAX_H, (openUp ? spaceAbove : spaceBelow) - 8),
        ...(openUp
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
      });
    }
    setQuery('');
    setIsOpen(true);
  };

  const toggleOption = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const q = query.trim().toLowerCase();
  const visibleOptions = q
    ? options.filter((o) => o.label.toLowerCase().includes(q))
    : options;

  return (
    <div className="relative">
      <label className="block text-xs font-medium text-gray-500 mb-1 dark:text-gray-400">{label}</label>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => (isOpen ? setIsOpen(false) : openMenu())}
        className="w-full px-3 py-2 text-left text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 flex items-center justify-between"
      >
        <span className="text-gray-900 dark:text-white">
          {selected.length === 0
            ? `Select ${label.toLowerCase()}`
            : `${selected.length} selected`}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''} dark:text-gray-500`} />
      </button>

      {isOpen && createPortal(
        <>
          {/* data-filter-menu: this menu is portalled out of the filter panel,
              so hosts' outside-click handlers must skip it (see TicketsPage). */}
          <div data-filter-menu className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div
            data-filter-menu
            style={menuStyle}
            className="z-50 flex flex-col bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden"
          >
            {searchable && (
              <div className="p-2 border-b border-gray-100 dark:border-gray-700">
                <input
                  type="text"
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={`Search ${label.toLowerCase()}...`}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            )}
            <div className="flex-1 overflow-y-auto py-1">
              {visibleOptions.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500">No matches</div>
              )}
              {visibleOptions.map((option) => {
                const Icon = option.icon;
                const isSelected = selected.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => toggleOption(option.value)}
                    className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                      isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                    }`}
                  >
                    <div className={`h-4 w-4 border rounded flex items-center justify-center ${
                      isSelected ? 'bg-primary-600 border-primary-600' : 'border-gray-300 dark:border-gray-500'
                    }`}>
                      {isSelected && <Check className="h-3 w-3 text-white" />}
                    </div>
                    {Icon && <Icon className="h-4 w-4 text-gray-400 dark:text-gray-500" />}
                    <span className={option.color || ''}>{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}

interface AdvancedFiltersProps {
  filters: TicketFilters;
  onChange: (filters: TicketFilters) => void;
  onClose: () => void;
  currentUserId?: string;
  availableTags?: string[];
  availableAgents?: { id: string; name: string }[];
}

export function AdvancedFilters({
  filters,
  onChange,
  onClose,
  currentUserId,
  availableTags = ['bug', 'feature', 'question', 'urgent', 'billing', 'technical'],
  availableAgents = [],
}: AdvancedFiltersProps) {
  const [localFilters, setLocalFilters] = useState<TicketFilters>(filters);
  const [ticketTypes, setTicketTypes] = useState<TicketType[]>([]);
  const [origins, setOrigins] = useState<TicketOriginOption[]>([]);
  const [roleScopedAgents, setRoleScopedAgents] = useState<{ id: string; name: string }[]>([]);
  const [tenantTags, setTenantTags] = useState<string[]>([]);
  const [teams, setTeams] = useState<TeamWithMembers[]>([]);

  // Role-aware assignee scope:
  //   - admin/owner  → every active agent & supervisor in the tenant
  //   - supervisor   → members of the teams they supervise
  //   - agent        → no Assignee filter (they only ever see their own tickets)
  const { user } = useAuthStore();
  const role = (user?.role || '').toLowerCase();
  const isAdminLevel = role === 'admin' || role === 'owner';
  const isSupervisor = role === 'supervisor';
  const showAssigneeFilter = isAdminLevel || isSupervisor;

  // Load active ticket types for the Ticket Type filter dropdown. Failure is
  // non-fatal — the control simply shows no options rather than breaking the
  // rest of the filter panel.
  useEffect(() => {
    let cancelled = false;
    ticketTypesService
      .getTicketTypes({ active: true })
      .then((types) => {
        if (!cancelled) setTicketTypes(types);
      })
      .catch(() => {
        if (!cancelled) setTicketTypes([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the tenant's ticket sources for the Source filter. Non-fatal on
  // failure (e.g. a ticket-service without the endpoint): no options.
  useEffect(() => {
    let cancelled = false;
    ticketService
      .getOrigins()
      .then((list) => {
        if (!cancelled) setOrigins(list);
      })
      .catch(() => {
        if (!cancelled) setOrigins([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Teams for the Team filter — the caller's own teams unless they are an
  // admin (the BFF enforces the same rule). Non-fatal: no options on failure.
  useEffect(() => {
    let cancelled = false;
    teamsService
      .getTeams({ isActive: true, limit: 200 })
      .then((res) => {
        if (!cancelled) setTeams(res.data);
      })
      .catch(() => {
        if (!cancelled) setTeams([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const teamOptions = orderTeamsForPicker(
    teamsInViewerScope(teams, { userId: user?.id, isAdmin: isAdminLevel }),
  );
  const selectedTeam = teams.find((t) => t.id === localFilters.teamId);
  const selectedTeamIsParent =
    !!selectedTeam && (isParentTeam(selectedTeam) || teams.some((t) => t.parentTeamId === selectedTeam.id));

  const sourceOptions = origins.map((o) => {
    const display = getTicketSourceDisplay(o);
    return {
      value: o.origin,
      label: display?.label ?? o.origin,
      icon: display ? getTicketSourceIcon(display) : undefined,
    };
  });
  const sourceLabel = (value: string) =>
    sourceOptions.find((o) => o.value === value)?.label || value;

  // Load the tenant's live tags for the Tags filter — the same distinct,
  // usage-ranked list the ticket sidebar and bulk bar use (GET /tags/suggestions),
  // so a tag added on any ticket becomes filterable here too. Failure is
  // non-fatal: we fall back to the `availableTags` prop default.
  useEffect(() => {
    let cancelled = false;
    tagService
      .suggestions(undefined, 100)
      .then((list) => {
        if (!cancelled) setTenantTags(list.map((t) => t.name));
      })
      .catch(() => {
        // suggestions are non-critical — leave tenantTags empty to use the fallback
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the role-scoped assignee list. Like ticket types, failures are
  // non-fatal — the dropdown just falls back to Anyone / Unassigned.
  useEffect(() => {
    if (!showAssigneeFilter) return;
    let cancelled = false;
    const toOption = (m: TeamMember) => ({
      id: m.id,
      name:
        m.displayName?.trim() ||
        [m.firstName, m.lastName].filter(Boolean).join(' ').trim() ||
        m.email,
    });
    (async () => {
      try {
        if (isAdminLevel) {
          const res = await teamMembersService.getTeamMembers({ status: 'active', limit: 200 });
          const opts = res.data
            .filter((m) => m.role === 'agent' || m.role === 'supervisor')
            .map(toOption);
          if (!cancelled) setRoleScopedAgents(opts);
        } else if (isSupervisor && user?.id) {
          const res = await teamsService.getTeams({ supervisorId: user.id, isActive: true });
          // A supervisor may own several teams; dedupe agents across them.
          const byId = new Map<string, { id: string; name: string }>();
          res.data.forEach((team) =>
            (team.agents || []).forEach((a) => byId.set(a.id, toOption(a))),
          );
          if (!cancelled) setRoleScopedAgents(Array.from(byId.values()));
        }
      } catch {
        if (!cancelled) setRoleScopedAgents([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showAssigneeFilter, isAdminLevel, isSupervisor, user?.id]);

  // Prefer an explicitly-provided list (back-compat) else the role-scoped one,
  // sorted by name for a predictable dropdown order.
  const assigneeOptions = (availableAgents.length > 0 ? availableAgents : roleScopedAgents)
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));

  // Tag options: prefer the live tenant tags; fall back to the prop default only
  // when the fetch returned nothing. Always union in any currently-selected tags
  // (e.g. from a saved filter) so their checkbox still renders even if they're
  // not in the suggestion list.
  const baseTags = tenantTags.length > 0 ? tenantTags : availableTags;
  const tagOptions = Array.from(
    new Set([...baseTags, ...(localFilters.tags || [])]),
  ).map((tag) => ({ value: tag, label: tag }));

  const {
    savedFilters,
    activeFilterId,
    isModified,
    fetchSavedFilters,
    createSavedFilter,
    updateSavedFilter,
    deleteSavedFilter,
    setActiveFilter,
    setModified,
    setDefault,
    unsetDefault,
    trackUsage,
    getMyFilters,
  } = useSavedFiltersStore();

  // Fetch saved filters on mount
  useEffect(() => {
    fetchSavedFilters();
  }, [fetchSavedFilters]);

  // Migrate localStorage filters on first load (one-time)
  useEffect(() => {
    const migrated = localStorage.getItem('savedTicketFiltersMigrated');
    if (migrated) return;

    const localData = localStorage.getItem('savedTicketFilters');
    if (!localData) {
      localStorage.setItem('savedTicketFiltersMigrated', 'true');
      return;
    }

    try {
      const localFilters = JSON.parse(localData) as Array<{ name: string; filters: any }>;
      if (localFilters.length > 0) {
        // Auto-migrate without prompting to keep it simple
        Promise.all(
          localFilters.map((lf) =>
            createSavedFilter({
              name: lf.name,
              filters: lf.filters,
              visibility: 'private',
            }).catch(() => null) // skip failures (e.g. duplicate names)
          )
        ).then(() => {
          localStorage.removeItem('savedTicketFilters');
          localStorage.setItem('savedTicketFiltersMigrated', 'true');
          fetchSavedFilters();
        });
      }
    } catch {
      localStorage.setItem('savedTicketFiltersMigrated', 'true');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateFilter = <K extends keyof TicketFilters>(key: K, value: TicketFilters[K]) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
    if (activeFilterId) {
      setModified(true);
    }
  };

  const applyFilters = () => {
    onChange(localFilters);
    onClose();
  };

  const resetFilters = () => {
    setLocalFilters({});
    setActiveFilter(null);
    // Immediately apply the cleared filters to the board and close the panel,
    // so the user doesn't have to click "Apply Filters" after resetting.
    onChange({});
    onClose();
  };

  const handleSaveFilter = async (
    name: string,
    options: { isDefault: boolean; visibility: 'private' | 'public' },
  ) => {
    await createSavedFilter({
      name,
      filters: serializeFilters(localFilters),
      visibility: options.visibility,
      isDefault: options.isDefault,
    });
  };

  const handleUpdateExisting = async (filter: UISavedFilter) => {
    await updateSavedFilter(filter.id, {
      filters: serializeFilters(localFilters),
    });
  };

  const handleSelectSavedFilter = (filter: UISavedFilter) => {
    const serverFilter = savedFilters.find((f) => f.id === filter.id);
    if (serverFilter) {
      const deserialized = deserializeFilters(serverFilter.filters as Record<string, any>);
      setLocalFilters(deserialized);
      setActiveFilter(serverFilter.id);
      trackUsage(serverFilter.id);
    }
  };

  const handleDeleteFilter = async (filterId: string) => {
    await deleteSavedFilter(filterId);
  };

  const handleSetDefault = async (filterId: string) => {
    await setDefault(filterId);
  };

  const handleUnsetDefault = async (filterId: string) => {
    await unsetDefault(filterId);
  };

  // Convert SavedFilter (service) to UISavedFilter (UI component) format
  const uiSavedFilters: UISavedFilter[] = savedFilters.map((f) => ({
    id: f.id,
    name: f.name,
    description: f.description,
    filters: f.filters as Record<string, any>,
    visibility: f.visibility as 'private' | 'public',
    isDefault: f.isDefault,
    createdBy: f.createdBy,
    createdAt: f.createdAt,
    updatedAt: f.updatedAt,
  }));

  const myOwnedFilters = currentUserId
    ? uiSavedFilters.filter((f) => f.createdBy === currentUserId)
    : uiSavedFilters;

  // includeSubTeams only qualifies the Team filter; it is not a filter of its own.
  const activeFilterCount = Object.entries(localFilters).filter(
    ([k, v]) => k !== 'includeSubTeams' && v !== undefined && (Array.isArray(v) ? v.length > 0 : true)
  ).length;

  const activeFilter = activeFilterId
    ? savedFilters.find((f) => f.id === activeFilterId)
    : null;

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg flex flex-col max-h-[80vh]">
      {/* Header — pinned. Reset / Save Filter / Apply Filters live here beside
          the close button; the body below scrolls underneath. */}
      <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filters</h3>
          {activeFilterCount > 0 && (
            <Badge variant="info">{activeFilterCount} active</Badge>
          )}
          {activeFilter && (
            <span className="text-sm text-gray-500 dark:text-gray-400 truncate">
              {isModified ? `${activeFilter.name} (modified)` : activeFilter.name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button variant="ghost" size="sm" onClick={resetFilters}>
            <RotateCcw className="h-4 w-4 mr-1" />
            Reset
          </Button>
          {activeFilterCount > 0 && (
            <SaveFilterButton
              onSave={handleSaveFilter}
              savedFilters={isModified && activeFilter ? myOwnedFilters : []}
              onSelectExisting={isModified ? handleUpdateExisting : undefined}
              buttonText={isModified && activeFilter ? 'Save changes' : 'Save Filter'}
              size="sm"
            />
          )}
          <Button size="sm" onClick={applyFilters}>
            Apply Filters
          </Button>
          <button
            onClick={onClose}
            aria-label="Close filters"
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 dark:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-6">

      {/* Saved Filters Section */}
      {savedFilters.length > 0 && (
        <SavedFiltersSection
          savedFilters={uiSavedFilters}
          activeFilterId={activeFilterId || undefined}
          currentUserId={currentUserId}
          onSelect={handleSelectSavedFilter}
          onSave={handleSaveFilter}
          onDelete={handleDeleteFilter}
          onSetDefault={handleSetDefault}
          onUnsetDefault={handleUnsetDefault}
          showSaveButton={false}
        />
      )}

      {/* Filter Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Status */}
        <MultiSelect
          label="Status"
          options={STATUS_OPTIONS}
          selected={localFilters.status || []}
          onChange={(value) => updateFilter('status', value.length > 0 ? value : undefined)}
        />

        {/* Priority */}
        <MultiSelect
          label="Priority"
          options={PRIORITY_OPTIONS}
          selected={localFilters.priority || []}
          onChange={(value) => updateFilter('priority', value.length > 0 ? value : undefined)}
        />

        {/* Channel */}
        <MultiSelect
          label="Channel"
          options={CHANNEL_OPTIONS}
          selected={localFilters.channel || []}
          onChange={(value) => updateFilter('channel', value.length > 0 ? value : undefined)}
        />

        {/* SLA Status */}
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">SLA Status</label>
          <ThemedSelect
            value={localFilters.slaStatus || ''}
            onChange={(e) => updateFilter('slaStatus', e.target.value as TicketFilters['slaStatus'] || undefined)}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Any</option>
            {SLA_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </ThemedSelect>
        </div>

        {/* Assignee — hidden for agents, who only ever see their own tickets.
            Multi-select with checkboxes, consistent with Status/Priority/Tags.
            No selection means "Anyone". Hidden while "Assigned to me" is on,
            since that override supersedes any assignee selection. */}
        {showAssigneeFilter && !localFilters.assignedToMe && (
          <MultiSelect
            label="Assignee"
            options={[
              { value: 'unassigned', label: 'Unassigned' },
              ...assigneeOptions.map((agent) => ({ value: agent.id, label: agent.name })),
            ]}
            selected={localFilters.assigneeIds || []}
            onChange={(value) => updateFilter('assigneeIds', value.length > 0 ? value : undefined)}
            searchable
          />
        )}

        {/* Assignee role — the assignee, a secondary agent working the ticket, or either. */}
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Assignee role</label>
          <ThemedSelect
            value={localFilters.assigneeRole || ''}
            onChange={(e) =>
              updateFilter('assigneeRole', (e.target.value as TicketFilters['assigneeRole']) || undefined)
            }
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Assignee or secondary agent</option>
            <option value="primary">Assignee only</option>
            <option value="secondary">Secondary agent only</option>
          </ThemedSelect>
        </div>

        {/* Ticket Type — multi-select with checkboxes, consistent with the
            other filters. No selection means "Any type". */}
        <MultiSelect
          label="Ticket Type"
          options={ticketTypes.map((type) => ({ value: type.id, label: type.name }))}
          selected={localFilters.ticketTypeIds || []}
          onChange={(value) => updateFilter('ticketTypeIds', value.length > 0 ? value : undefined)}
          searchable
        />

        {/* Team — a parent offers "Include sub-teams" (on by default). */}
        {teamOptions.length > 0 && (
          <div>
            <label
              htmlFor="ticket-filter-team"
              className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1"
            >
              Team
            </label>
            <ThemedSelect
              id="ticket-filter-team"
              value={localFilters.teamId || ''}
              onChange={(e) => {
                const teamId = e.target.value || undefined;
                const team = teams.find((t) => t.id === teamId);
                // Always ask for the team TREE. For a leaf team that is just the
                // team itself plus its agents' no-team tickets: a ticket assigned
                // by hand to a team's agent keeps ticket.teamId null, and the
                // exact-teamId match hid it from the board. A parent team can
                // still be narrowed to its own queue via the checkbox below.
                setLocalFilters((prev) => ({
                  ...prev,
                  teamId,
                  includeSubTeams: team ? true : undefined,
                }));
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Any team</option>
              {teamOptions.map((team) => (
                <option key={team.id} value={team.id}>
                  {teamOptionLabel(team)}
                </option>
              ))}
            </ThemedSelect>
            {selectedTeamIsParent && (
              <label className="mt-1.5 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={!!localFilters.includeSubTeams}
                  onChange={(e) => updateFilter('includeSubTeams', e.target.checked || undefined)}
                />
                Include sub-teams
              </label>
            )}
          </div>
        )}

        {/* Source — where the ticket came from: an API-key product, a
            channel, or an agent. Options from GET /tickets/origins. */}
        <MultiSelect
          label="Source"
          options={sourceOptions}
          selected={localFilters.origins || []}
          onChange={(value) => updateFilter('origins', value.length > 0 ? value : undefined)}
          searchable
        />

        {/* Tags */}
        <MultiSelect
          label="Tags"
          options={tagOptions}
          selected={localFilters.tags || []}
          onChange={(value) => updateFilter('tags', value.length > 0 ? value : undefined)}
          searchable
        />
      </div>

      {/* Date Range */}
      <div>
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
          <Calendar className="h-3 w-3 inline mr-1" />
          Date Range
        </label>
        <div className="flex flex-wrap gap-2 mb-3">
          {DATE_PRESETS.map((preset) => (
            <button
              key={preset.label}
              type="button"
              onClick={() => updateFilter('dateRange', preset.getValue())}
              className="px-3 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors"
            >
              {preset.label}
            </button>
          ))}
          {localFilters.dateRange && (
            <button
              type="button"
              onClick={() => updateFilter('dateRange', undefined)}
              className="px-3 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 dark:text-red-300 dark:bg-red-900/30 dark:hover:bg-red-900/50 rounded-full transition-colors"
            >
              Clear
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <input
              type="date"
              value={localFilters.dateRange?.from ? format(new Date(localFilters.dateRange.from), 'yyyy-MM-dd') : ''}
              onChange={(e) => {
                const from = e.target.value ? new Date(e.target.value) : undefined;
                if (from) {
                  updateFilter('dateRange', {
                    from,
                    to: localFilters.dateRange?.to || endOfDay(new Date()),
                  });
                }
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <span className="text-gray-400 dark:text-gray-500">to</span>
          <div className="flex-1">
            <input
              type="date"
              value={localFilters.dateRange?.to ? format(new Date(localFilters.dateRange.to), 'yyyy-MM-dd') : ''}
              onChange={(e) => {
                const to = e.target.value ? new Date(e.target.value) : undefined;
                if (to) {
                  updateFilter('dateRange', {
                    from: localFilters.dateRange?.from || startOfDay(subDays(new Date(), 30)),
                    to,
                  });
                }
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Active Filters Summary */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          {localFilters.status?.map((s) => (
            <Badge key={s} variant="info" className="gap-1">
              Status: {s}
              <button
                onClick={() => updateFilter('status', localFilters.status?.filter((v) => v !== s))}
                aria-label={`Remove status filter: ${s}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {localFilters.priority?.map((p) => (
            <Badge key={p} variant="warning" className="gap-1">
              Priority: {p}
              <button
                onClick={() => updateFilter('priority', localFilters.priority?.filter((v) => v !== p))}
                aria-label={`Remove priority filter: ${p}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {localFilters.channel?.map((c) => (
            <Badge key={c} variant="default" className="gap-1">
              Channel: {c}
              <button
                onClick={() => updateFilter('channel', localFilters.channel?.filter((v) => v !== c))}
                aria-label={`Remove channel filter: ${c}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {localFilters.slaStatus && (
            <Badge variant="error" className="gap-1">
              SLA: {localFilters.slaStatus}
              <button
                onClick={() => updateFilter('slaStatus', undefined)}
                aria-label={`Remove SLA status filter: ${localFilters.slaStatus}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {localFilters.assignedToMe && (
            <Badge variant="info" className="gap-1">
              Assigned to me
              <button
                onClick={() => updateFilter('assignedToMe', undefined)}
                aria-label="Remove assigned-to-me filter"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {localFilters.assigneeIds?.map((id) => (
            <Badge key={id} variant="default" className="gap-1">
              Assignee: {id === 'unassigned'
                ? 'Unassigned'
                : assigneeOptions.find((a) => a.id === id)?.name || id}
              <button
                onClick={() => {
                  const next = localFilters.assigneeIds?.filter((v) => v !== id);
                  updateFilter('assigneeIds', next && next.length > 0 ? next : undefined);
                }}
                aria-label={`Remove assignee filter: ${id === 'unassigned' ? 'Unassigned' : assigneeOptions.find((a) => a.id === id)?.name || id}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {localFilters.teamId && (
            <Badge variant="default" className="gap-1">
              Team: {selectedTeam?.name || 'Selected team'}
              {selectedTeamIsParent && !localFilters.includeSubTeams ? ' (own queue)' : ''}
              <button
                onClick={() =>
                  setLocalFilters((prev) => ({ ...prev, teamId: undefined, includeSubTeams: undefined }))
                }
                aria-label="Remove team filter"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {localFilters.ticketTypeIds?.map((id) => (
            <Badge key={id} variant="default" className="gap-1">
              Type: {ticketTypes.find((t) => t.id === id)?.name || id}
              <button
                onClick={() => {
                  const next = localFilters.ticketTypeIds?.filter((v) => v !== id);
                  updateFilter('ticketTypeIds', next && next.length > 0 ? next : undefined);
                }}
                aria-label={`Remove ticket type filter: ${ticketTypes.find((t) => t.id === id)?.name || id}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {localFilters.origins?.map((o) => (
            <Badge key={o} variant="default" className="gap-1">
              Source: {sourceLabel(o)}
              <button
                onClick={() => {
                  const next = localFilters.origins?.filter((v) => v !== o);
                  updateFilter('origins', next && next.length > 0 ? next : undefined);
                }}
                aria-label={`Remove source filter: ${sourceLabel(o)}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {localFilters.tags?.map((t) => (
            <Badge key={t} variant="default" className="gap-1">
              <Tag className="h-3 w-3" />
              {t}
              <button
                onClick={() => updateFilter('tags', localFilters.tags?.filter((v) => v !== t))}
                aria-label={`Remove tag filter: ${t}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {localFilters.dateRange && (
            <Badge variant="default" className="gap-1">
              <Clock className="h-3 w-3" />
              {format(new Date(localFilters.dateRange.from), 'MMM d')} - {format(new Date(localFilters.dateRange.to), 'MMM d')}
              <button
                onClick={() => updateFilter('dateRange', undefined)}
                aria-label="Remove date range filter"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
        </div>
      )}

      </div>
    </div>
  );
}
